import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { ExtendedDateRange, ScheduleTimeFrame } from 'common/contracts';
import { EIntervalsRepeat } from 'constants/enums';
import { timeUtils } from 'utils/scheduleUtils';

import RichText from 'components/RichText/RichText';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from 'components/ui/tabs';

import { ScheduleDateRange } from './components/ScheduleDateRange';
import { ScheduleInterval } from './components/ScheduleInterval';
import { SchedulePermanent } from './components/SchedulePermanent';
import {
  deriveInitialScheduleTab,
  type ScheduleTabId
} from './OfferScheduleSection.utils';

interface IOfferScheduleSectionProps {
  startHour: string;
  setStartHour: (hour: string) => void;
  intervalCron: string;
  setIntervalCron: (cron: string) => void;
  showIntervals?: boolean;
  showScheduleAvailability?: boolean;
}

const OfferScheduleSection: React.FC<IOfferScheduleSectionProps> = ({
  startHour,
  setStartHour,
  intervalCron,
  setIntervalCron,
  showIntervals = true,
  showScheduleAvailability = true
}): React.ReactElement => {
  const form = useFormContext();
  const schedule = form.watch('schedule');

  const [activeTab, setActiveTab] = useState<ScheduleTabId>(() =>
    deriveInitialScheduleTab(form.getValues('schedule'), showIntervals)
  );
  const [repeat, setRepeat] = useState<EIntervalsRepeat>(
    EIntervalsRepeat.NONE
  );
  const [weekDay, setWeekDay] = useState<number>(0);
  const [dayInMonth, setDayInMonth] = useState<number>(1);
  const [appearancesTableData, setAppearancesTableData] = useState<
    ScheduleTimeFrame[]
  >([]);
  const [editRangeDatesValues, setEditRangeDatesValues] =
    useState<ExtendedDateRange | null>(null);

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      );

    setAppearancesTableData(timeFramesData);
  }, [schedule?.timeFrames]);

  const generateCron = useCallback((): void => {
    if (repeat === EIntervalsRepeat.NONE) {
      setIntervalCron('');
      return;
    }

    if (!startHour) {
      return;
    }

    const newCron = timeUtils.generateCronString(
      repeat,
      startHour,
      weekDay,
      dayInMonth
    );

    if (newCron && newCron !== intervalCron) {
      setIntervalCron(newCron);
    }
  }, [repeat, startHour, weekDay, dayInMonth, intervalCron, setIntervalCron]);

  useEffect(() => {
    generateCron();
  }, [generateCron]);

  useEffect(() => {
    if (schedule?.permanent && intervalCron) {
      form.setValue(
        'schedule',
        {
          permanent: schedule?.permanent,
          timeFrames: schedule?.timeFrames,
          ...(repeat !== EIntervalsRepeat.NONE && { interval: intervalCron })
        },
        { shouldDirty: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalCron, repeat]);

  useEffect(() => {
    const interval = schedule?.interval;

    if (!interval) {
      return;
    }

    const { dayInMonthFromCron, hourFromCron, weekDayFromCron } =
      timeUtils.getTimeFromCron(interval);

    let newRepeat = EIntervalsRepeat.NONE;
    if (dayInMonthFromCron) {
      newRepeat = EIntervalsRepeat.MONTHLY;
    } else if (weekDayFromCron !== undefined) {
      newRepeat = EIntervalsRepeat.WEEKLY;
    } else if (hourFromCron) {
      newRepeat = EIntervalsRepeat.DAILY;
    }

    if (newRepeat !== repeat) {
      setRepeat(newRepeat);
    }
    if (dayInMonthFromCron && dayInMonthFromCron !== dayInMonth) {
      setDayInMonth(dayInMonthFromCron);
    }
    if (weekDayFromCron !== undefined && weekDayFromCron !== weekDay) {
      setWeekDay(weekDayFromCron);
    }
    if (hourFromCron && hourFromCron !== startHour) {
      setStartHour(hourFromCron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.interval]);

  const resetScheduleValues = useCallback((): void => {
    setEditRangeDatesValues(null);
    setStartHour('');
    setIntervalCron('');
    setRepeat(EIntervalsRepeat.NONE);
  }, [setStartHour, setIntervalCron]);

  const handleTabChange = useCallback(
    (next: string): void => {
      const tab = next as ScheduleTabId;
      setActiveTab(tab);
      const baseTimeFrames = form.getValues('schedule')?.timeFrames ?? [];
      if (tab === 'schedule') {
        form.setValue(
          'schedule',
          { permanent: false, timeFrames: baseTimeFrames },
          { shouldDirty: true }
        );
        return;
      }
      form.setValue(
        'schedule',
        {
          permanent: true,
          timeFrames: baseTimeFrames,
          ...(intervalCron && { interval: intervalCron })
        },
        { shouldDirty: true }
      );
    },
    [form, intervalCron]
  );

  const tabTriggers = useMemo((): { value: string; label: string }[] => {
    const tabs = [
      { value: 'permanent', label: 'Permanent' },
      ...(showIntervals
        ? [{ value: 'intervals', label: 'Intervals' }]
        : []),
      { value: 'schedule', label: 'Schedule' }
    ];
    return tabs;
  }, [showIntervals]);

  const gridColsClass = showIntervals ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="flex flex-col mb-8">
      <RichText
        title="Scheduled Offer"
        description="Set specific dates & hours to your offer"
        className="mb-9"
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList
          className={`bg-zinc-100 p-1 rounded-xl grid ${gridColsClass} h-10 w-full`}
        >
          {tabTriggers.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 text-zinc-500 rounded-lg font-normal h-8"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="permanent" className="mt-9">
          <SchedulePermanent />
        </TabsContent>

        {showIntervals && (
          <TabsContent value="intervals" className="mt-9">
            <ScheduleInterval
              repeat={repeat}
              setRepeat={setRepeat}
              startHour={startHour}
              setStartHour={setStartHour}
              weekDay={weekDay}
              setWeekDay={setWeekDay}
              dayInMonth={dayInMonth}
              setDayInMonth={setDayInMonth}
            />
          </TabsContent>
        )}

        <TabsContent value="schedule" className="mt-9">
          <ScheduleDateRange
            editRangeDatesValues={editRangeDatesValues}
            setEditRangeDatesValues={setEditRangeDatesValues}
            appearancesTableData={appearancesTableData}
            resetScheduleValues={resetScheduleValues}
            showAvailability={showScheduleAvailability}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export { OfferScheduleSection };
